import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";

import { AffectedSubscriptionType, NotificationType } from "../../apis/networkdashboard/graphql/graphql";
import { HealthRecord } from "../../apis/stats/rest/models";
import { HealthService } from "../../apis/stats/rest/services";
import { FooterComponent } from "../../components/common/footer/footer";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { EMPTY_HEALTH } from "../../components/models/HealthIndicator";
import { safeResource } from "../../helpers/safe-resource";
import { collectLocations } from "../../helpers/subscription/collect-locations";
import { collectTags } from "../../helpers/subscription/collect-tags";
import { AuthService } from "../../services/authservice";
import { ServiceDisruptionService } from "../../services/service-disruption.service";
import { SubscriptionListItem, SubscriptionListService } from "../../services/subscription-list.service";
import { getFilteredItems } from "./helpers/get-filtered-items";
import { SubscriptionsFilterComponent } from "./subscriptions-filter";
import { AugmentedSubscription, SubscriptionsResultsComponent } from "./subscriptions-results";

@Component({
  selector: "page-subscriptions",
  templateUrl: "subscriptions.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FooterComponent, NavigationComponent, SubscriptionsFilterComponent, SubscriptionsResultsComponent],
})
export class SubscriptionsPage {
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);
  private subscriptionListService = inject(SubscriptionListService);
  private serviceDisruptionService = inject(ServiceDisruptionService);
  private healthService = inject(HealthService);

  protected productType = signal(this.extractProductType());
  private userLoaded = toSignal(this.auth.userLoaded, { initialValue: false });

  protected healthRecords = computed(() => this._healthRecords.safeValue());
  private _healthRecords = safeResource({
    params: () => ({ subscriptions: this._subscriptions.safeValue() }),
    loader: ({ params }) => this.fetchHealthRecords(params.subscriptions),
    defaultValue: {} as Record<string, HealthRecord>,
  });

  private subscriptions = computed(() => this._subscriptions.safeValue());
  private _subscriptions = safeResource({
    params: () => ({ loaded: this.userLoaded(), productType: this.productType() }),
    loader: ({ params }) =>
      params.loaded ?
        firstValueFrom(this.subscriptionListService.fetch(params.productType))
      : Promise.resolve([] as SubscriptionListItem[]),
    defaultValue: [] as SubscriptionListItem[],
  });

  protected serviceDisruptions = computed(() => this._serviceDisruptions.value());
  private _serviceDisruptions = safeResource({
    params: () => ({
      subscriptionsIds: this.subscriptions().map((subscription) => subscription.subscriptionId),
    }),
    loader: async ({ params }) => {
      const ids = params.subscriptionsIds;
      return ids.length > 0 ? this.serviceDisruptionService.fetchForSubscriptions(ids) : ([] as NotificationType[]);
    },
    defaultValue: [] as NotificationType[],
  });

  protected augmentedSubscriptions = computed<AugmentedSubscription[]>(() =>
    this.subscriptions().map((subscription) => {
      const identifySubscription = (s: AffectedSubscriptionType) => s.subscriptionId === subscription.subscriptionId;
      const disruption = this.serviceDisruptions().find((d) => d.affectedSubscriptions.find(identifySubscription));
      const health = this.healthRecords()[subscription.subscriptionId] ?? EMPTY_HEALTH;
      return { subscription, disruption, health };
    }),
  );

  protected filteredSubscriptions = computed(() =>
    getFilteredItems(
      this.augmentedSubscriptions(),
      this.activeFilters(),
      this.activeTags(),
      this.activeLocations(),
      this.servicesSearch(),
    ),
  );

  protected activeFilters = signal<string[]>([]);
  protected activeTags = signal<string[]>([]);
  protected activeLocations = signal<string[]>([]);
  protected servicesSearch = signal<string>("");

  protected collectedTags = computed(() => collectTags(this.subscriptions()));
  protected collectedLocations = computed(() => collectLocations(this.subscriptions()));

  private fetchHealthRecords = async (subscriptions: SubscriptionListItem[]): Promise<Record<string, HealthRecord>> => {
    const statuses = subscriptions.map(async (s) => {
      const service = { subscription_id: s.healthSubscriptionId ?? s.subscriptionId };
      const failSafely = () => {
        console.warn(`Failed fetching health for service ${service}`);
        return EMPTY_HEALTH;
      };
      const status = this.healthService.getNetworkHealthByServiceId(service).catch(failSafely);
      return status.then((health) => ({ [s.subscriptionId]: health }));
    });

    // Assign all health statuses to an object so we can index by id
    return Promise.all(statuses).then((ss) => Object.assign({}, ...ss));
  };

  private extractProductType(): string {
    let productType = this.route.snapshot.paramMap.get("productType");
    if (!productType) {
      const { routeConfig } = this.route.snapshot;
      const pathDirs = (routeConfig?.path ?? "").split("/");
      productType = pathDirs?.[pathDirs.length - 1];
    }

    if (!productType) {
      throw Error("Unable to load subscription list page: product type unknown");
    }

    return productType;
  }
}
